// Forgot password + reset password + email verification pages.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock } from "lucide-react";
import { customerApi } from "@/lib/customerAuth";

function AuthShell({ title, children }) {
    return (
        <section className="min-h-screen bg-ivory pt-24 pb-16">
            <div className="max-w-md mx-auto px-6">
                <div className="text-center mb-8">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold">Serenity Cove</div>
                    <h1 className="mt-4 font-display text-4xl md:text-5xl text-navy">{title}</h1>
                </div>
                <div className="bg-white border border-navy/10 rounded-2xl p-8 shadow-sm">{children}</div>
            </div>
        </section>
    );
}

export function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await customerApi.post("/customer/auth/forgot-password", { email });
            setSent(true);
        } catch {
            toast.error("Could not send reset email");
        } finally { setBusy(false); }
    };
    return (
        <AuthShell title="Forgot password">
            {sent ? (
                <div data-testid="forgot-sent" className="text-center py-4">
                    <p className="text-navy/70 text-sm">If an account exists for <b>{email}</b>, we've emailed a reset link. Check your inbox.</p>
                    <Link to="/signin" className="mt-6 inline-block text-gold hover:underline text-sm">Back to sign in</Link>
                </div>
            ) : (
                <form onSubmit={submit} data-testid="forgot-form" className="space-y-4">
                    <p className="text-navy/70 text-sm">Enter your account email and we'll send you a reset link.</p>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Email</span>
                        <div className="mt-2 relative">
                            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
                            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email"
                                className="w-full pl-9 pr-3 py-3 border border-navy/15 rounded-lg text-sm focus:border-gold focus:outline-none" />
                        </div>
                    </label>
                    <button type="submit" disabled={busy} data-testid="forgot-submit" className="w-full bg-navy text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-navy/90 disabled:opacity-60">
                        {busy ? "Sending…" : "Send reset link"}
                    </button>
                    <div className="text-center pt-2">
                        <Link to="/signin" className="text-sm text-navy/60 hover:text-gold">Back to sign in</Link>
                    </div>
                </form>
            )}
        </AuthShell>
    );
}

export function ResetPassword() {
    const { token } = useParams();
    const nav = useNavigate();
    const [pw, setPw] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await customerApi.post("/customer/auth/reset-password", { token, new_password: pw });
            toast.success("Password updated — please sign in");
            nav("/signin", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not reset password");
        } finally { setBusy(false); }
    };
    return (
        <AuthShell title="Choose a new password">
            <form onSubmit={submit} data-testid="reset-form" className="space-y-4">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">New password (min. 8)</span>
                    <div className="mt-2 relative">
                        <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
                        <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password"
                            className="w-full pl-9 pr-3 py-3 border border-navy/15 rounded-lg text-sm focus:border-gold focus:outline-none" />
                    </div>
                </label>
                <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full bg-navy text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-navy/90 disabled:opacity-60">
                    {busy ? "Updating…" : "Update password"}
                </button>
            </form>
        </AuthShell>
    );
}

export function VerifyEmail() {
    const { token } = useParams();
    const nav = useNavigate();
    const [status, setStatus] = useState("verifying"); // verifying | ok | error
    useEffect(() => {
        customerApi.post("/customer/auth/verify-email", { token })
            .then(() => setStatus("ok"))
            .catch(() => setStatus("error"));
    }, [token]);
    return (
        <AuthShell title={status === "ok" ? "Email verified" : status === "error" ? "Link expired" : "Verifying…"}>
            <div data-testid="verify-result" className="text-center py-4">
                {status === "verifying" && <p className="text-navy/70 text-sm">Confirming your email…</p>}
                {status === "ok" && (
                    <>
                        <p className="text-navy/70 text-sm">Thank you — your email is confirmed and your bookings are linked.</p>
                        <button onClick={() => nav("/account")} className="mt-6 bg-navy text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-navy/90">Go to my account</button>
                    </>
                )}
                {status === "error" && (
                    <>
                        <p className="text-navy/70 text-sm">This verification link is invalid or has expired. Sign in and we'll email a fresh one.</p>
                        <Link to="/signin" className="mt-6 inline-block text-gold hover:underline text-sm">Sign in</Link>
                    </>
                )}
            </div>
        </AuthShell>
    );
}
